"use client";

import { useEffect, useState, Suspense } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { authClient, authenticatedFetch, onSyncReady } from "../../lib/auth-client";
import { Search, Info, Plus } from "lucide-react";
import { FEATURE_FLAGS } from "../../lib/config";
import { CreateLobbyModal } from "@/components/CreateLobbyModal";
import { GameErrorBoundary } from "@/components/GameErrorBoundary";

interface Game {
    id: string;
    name: string;
    status: string;
    entryFee: number;
    playerCount: number;
    scheduledStart: string;
    categoryName?: string;
}

const CATEGORIES = [
    { id: 'trivia', name: 'Trivia', emoji: '🧠', color: 'bg-indigo-100 text-indigo-700', hover: 'hover:bg-indigo-200', border: 'border-indigo-200' },
    { id: 'sports', name: 'Sports', emoji: '⚽', color: 'bg-orange-100 text-orange-700', hover: 'hover:bg-orange-200', border: 'border-orange-200' },
    { id: 'history', name: 'History', emoji: '📜', color: 'bg-yellow-100 text-yellow-700', hover: 'hover:bg-yellow-200', border: 'border-yellow-200' },
    { id: 'science', name: 'Science', emoji: '🔬', color: 'bg-green-100 text-green-700', hover: 'hover:bg-green-200', border: 'border-green-200' },
    { id: 'entertainment', name: 'Entertainment', emoji: '🎬', color: 'bg-purple-100 text-purple-700', hover: 'hover:bg-purple-200', border: 'border-purple-200' },
    { id: 'geography', name: 'Geography', emoji: '🌍', color: 'bg-teal-100 text-teal-700', hover: 'hover:bg-teal-200', border: 'border-teal-200' },
    { id: 'music', name: 'Music', emoji: '🎵', color: 'bg-pink-100 text-pink-700', hover: 'hover:bg-pink-200', border: 'border-pink-200' },
    { id: 'technology', name: 'Technology', emoji: '💻', color: 'bg-cyan-100 text-cyan-700', hover: 'hover:bg-cyan-200', border: 'border-cyan-200' },
];

function GameLobbyContent() {
    const [games, setGames] = useState<Game[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const searchParams = useSearchParams();
    const [activeTab, setActiveTab] = useState<'free' | 'paid'>(
        (searchParams.get('tab') as 'free' | 'paid') || 'free'
    );
    const [isHydrated, setIsHydrated] = useState(false);
    const [isLobbyModalOpen, setIsLobbyModalOpen] = useState(false);
    const [walletBalance, setWalletBalance] = useState<number | null>(null);

    const { data: session } = authClient.useSession();
    const router = useRouter();

    useEffect(() => {
        onSyncReady(() => setIsHydrated(true));

        // Auto-open modal if requested (e.g. returning from onboarding)
        if (searchParams.get('openLobbyModal') === 'true') {
            setIsLobbyModalOpen(true);
        }
    }, [searchParams]);

    useEffect(() => {
        const fetchGames = async () => {
            try {
                setLoading(true);
                const res = await authenticatedFetch(`/api/v1/games?status=lobby`);
                if (res.ok) {
                    const data = await res.json();
                    setGames(data.games || []);
                }
            } catch (err) {
                console.error("Failed to fetch games", err);
            } finally {
                setLoading(false);
            }
        };

        if (isHydrated) {
            fetchGames();
            const interval = setInterval(fetchGames, 10000);
            return () => clearInterval(interval);
        }
    }, [isHydrated]);

    useEffect(() => {
        if (!session?.user || !isHydrated) return;
        const fetchBalance = async () => {
            try {
                const res = await authenticatedFetch(`/api/v1/wallet/${session.user.id}`);
                if (res.ok) {
                    const data = await res.json();
                    setWalletBalance(data.balance);
                }
            } catch (err) {
                console.error("Failed to fetch wallet balance", err);
            }
        };
        fetchBalance();
    }, [session, isHydrated]);

    const filteredGames = games.filter(g => {
        const matchesCategory = selectedCategory
            ? g.name.toLowerCase().includes(selectedCategory.toLowerCase()) || (g.categoryName && g.categoryName.toLowerCase() === selectedCategory.toLowerCase())
            : true;
        if (!matchesCategory) return false;
        const fee = Number(g.entryFee);
        if (activeTab === 'free') return fee === 0;
        if (activeTab === 'paid') return fee > 0;
        return true;
    });

    filteredGames.sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div className="min-h-screen bg-[#F8F7F4] text-gray-900 font-sans">
            {/* ===== HEADER ===== */}
            <header className="sticky top-0 z-40 backdrop-blur-md bg-[#F8F7F4]/80 border-b border-gray-200/50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                                <span className="text-indigo-600 font-black text-lg">O</span>
                            </div>
                            <span className="font-extrabold text-xl tracking-tight">OBVIO</span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link href="/" className="hidden md:block text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors">
                            Back to Home
                        </Link>
                        {session && FEATURE_FLAGS.ENABLE_AUTH ? (
                            <div className="flex items-center gap-3">
                                <Link href="/profile" className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-gray-200 shadow-sm hover:border-green-200 transition-colors group">
                                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 group-hover:bg-green-500 group-hover:text-white transition-colors">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></svg>
                                    </div>
                                    <span className="text-sm font-bold text-gray-700">£{(walletBalance || 0).toFixed(2)}</span>
                                </Link>
                                <Link href="/profile" className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-gray-200 shadow-sm hover:border-indigo-200 transition-colors">
                                    <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600">
                                        {session.user.name?.[0] || 'U'}
                                    </div>
                                    <span className="text-sm font-bold text-gray-700 max-w-[100px] truncate">{session.user.name}</span>
                                </Link>
                                <button onClick={() => authClient.signOut()} className="p-2 text-gray-400 hover:text-red-500 transition-colors" title="Sign Out">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                                </button>
                            </div>
                        ) : (
                            FEATURE_FLAGS.ENABLE_AUTH && (
                                <Link href="/login" className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-800 transition-colors shadow-lg shadow-gray-200">
                                    Login
                                </Link>
                            )
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-12">
                <div className="text-center mb-12">
                    <p className="text-sm font-semibold text-indigo-500 tracking-widest uppercase mb-4">LIVE LOBBY</p>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
                        {selectedCategory
                            ? `${selectedCategory} Arena`
                            : (activeTab === 'paid' ? "High Rollers Arena" : "Choose Your Arena")}
                    </h1>
                    <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                        {(selectedCategory || activeTab === 'paid')
                            ? "Select a game room to start playing."
                            : "Select a category to view available games."}
                    </p>
                </div>

                {loading ? (
                    <div className="text-center py-20 animate-pulse text-gray-400 font-medium">
                        Loading available games...
                    </div>
                ) : (
                    <>
                        {/* VIEW 1: CATEGORY GRID */}
                        {!selectedCategory && activeTab !== 'paid' && (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    {CATEGORIES.map((cat) => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setSelectedCategory(cat.name)}
                                            className={`${cat.color} ${cat.hover} border ${cat.border} rounded-3xl p-6 text-left transition-all duration-300 hover:scale-105 hover:shadow-xl group flex flex-col justify-between h-40 md:h-48`}
                                        >
                                            <span className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300 block">{cat.emoji}</span>
                                            <span className="font-bold text-lg">{cat.name}</span>
                                        </button>
                                    ))}
                                </div>

                                {/* HOST PRIVATE GAME SECTION */}
                                <div className="mt-16 bg-white rounded-3xl p-10 border border-gray-100 shadow-sm overflow-hidden relative group">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50 rounded-full -mr-32 -mt-32 transition-transform duration-700 group-hover:scale-110"></div>
                                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                                        <div className="max-w-md text-center md:text-left">
                                            <div className="w-12 h-12 rounded-2xl bg-indigo-500 flex items-center justify-center text-white mb-6 shadow-lg shadow-indigo-200">
                                                <Plus size={28} />
                                            </div>
                                            <h2 className="text-3xl font-black mb-3 italic">Run Your Own Game.</h2>
                                            <p className="text-gray-500 font-medium">
                                                Host a private party for friends, colleagues, or your community.
                                                Choose between live matches or async &quot;blind play&quot;.
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setIsLobbyModalOpen(true)}
                                            className="bg-gray-900 text-white px-10 py-5 rounded-full font-black text-lg hover:bg-gray-800 transition-all hover:scale-105 shadow-xl shadow-gray-200 active:scale-95 whitespace-nowrap"
                                        >
                                            Create Private Lobby
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* VIEW 2: GAME LIST */}
                        {(selectedCategory || activeTab === 'paid') && (
                            <div className="animate-fade-in-up">
                                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                                    {selectedCategory ? (
                                        <button
                                            onClick={() => setSelectedCategory(null)}
                                            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 font-semibold transition-colors group"
                                        >
                                            <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-gray-200">←</span>
                                            Back to Categories
                                        </button>
                                    ) : <div className="hidden md:block"></div>}

                                    <div className="bg-gray-200 p-1 rounded-xl flex gap-1">
                                        <button
                                            onClick={() => setActiveTab('free')}
                                            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'free' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                        >
                                            FREE
                                        </button>
                                        {FEATURE_FLAGS.ENABLE_PAYMENTS && (
                                            <button
                                                onClick={() => setActiveTab('paid')}
                                                className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'paid' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                            >
                                                HIGH ROLLERS
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {filteredGames.length > 0 ? filteredGames.map((game) => (
                                        <div key={game.id} className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group">
                                            <div className={`absolute top-0 right-0 text-xs font-bold px-4 py-1.5 rounded-bl-xl ${game.status === 'live' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                                                {game.status.toUpperCase()}
                                            </div>
                                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 ${game.entryFee > 0 ? 'bg-indigo-50 text-indigo-600' : 'bg-green-50 text-green-600'}`}>
                                                <span className="text-2xl">{game.entryFee > 0 ? '💎' : '🎟️'}</span>
                                            </div>
                                            <h3 className="text-xl font-bold mb-2">{game.name}</h3>
                                            <div className="flex items-center gap-3 text-sm text-gray-500 mb-6 font-medium">
                                                <span className="flex items-center gap-1">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
                                                    {game.playerCount}
                                                </span>
                                                <span>•</span>
                                                <span>{new Date(game.scheduledStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                            <div className="space-y-3">
                                                <div className="flex justify-between items-center text-sm font-semibold">
                                                    <span className="text-gray-400">Entry Fee</span>
                                                    <span className={Number(game.entryFee) > 0 ? 'text-gray-900' : 'text-green-600'}>
                                                        {Number(game.entryFee) > 0 ? `£${game.entryFee}` : 'FREE'}
                                                    </span>
                                                </div>
                                                <div className="h-px bg-gray-100 w-full my-4"></div>
                                                <Link
                                                    href={Number(game.entryFee) > 0 && (!session || !FEATURE_FLAGS.ENABLE_AUTH) ? (FEATURE_FLAGS.ENABLE_AUTH ? "/login" : "/") : `/game/${game.id}`}
                                                    className={`block w-full text-center py-3.5 rounded-xl font-bold transition-all shadow-lg text-sm ${Number(game.entryFee) > 0 ? "bg-gray-900 text-white hover:bg-gray-800 shadow-gray-900/10" : "bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"}`}
                                                >
                                                    {Number(game.entryFee) > 0 ? (session && FEATURE_FLAGS.ENABLE_AUTH ? "Play Now (Paid)" : (FEATURE_FLAGS.ENABLE_AUTH ? "Login to Play" : "Coming Soon")) : "Play Now"}
                                                </Link>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="col-span-full bg-white rounded-3xl p-12 text-center border dashed border-2 border-gray-200">
                                            <div className="text-4xl mb-4">😴</div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                                {activeTab === 'paid' ? "No High Roller Games" : `No Free Games for ${selectedCategory}`}
                                            </h3>
                                            <p className="text-gray-500">Check back later!</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </main>

            <CreateLobbyModal
                isOpen={isLobbyModalOpen}
                onClose={() => setIsLobbyModalOpen(false)}
            />
        </div>
    );
}

export default function GameLobby() {
    return (
        <Suspense fallback={<div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center">Loading...</div>}>
            <GameErrorBoundary>
                <GameLobbyContent />
            </GameErrorBoundary>
        </Suspense>
    );
}
